import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Form, Input, Typography } from "antd";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { useT } from "@trust/i18n";
import { http } from "@/lib/api";
import { useAuthStore, type UserInfo } from "@/lib/auth";

interface LoginResp {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

/** 登录成功后的回跳目标:仅接受站内路径;next 指向 /inx/login 自身时回退 "/" 避免自指循环。 */
function safeNext(search: { next?: unknown }): string {
  const n = search.next;
  if (typeof n !== "string") return "/inx/regInx";
  if (!n.startsWith("/") || n.startsWith("//") || n.startsWith("/inx/login")) return "/inx/regInx";
  return n;
}

export function LoginPage() {
  const t = useT();
  const navigate = useNavigate();
  const { setTokens, setUser, loggedIn } = useAuthStore();
  const search = useRouterState({
    select: (s) => s.location.search as { next?: unknown },
  });
  const next = safeNext(search);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (loggedIn) {
    navigate({ to: next });
    return null;
  }

  async function onFinish(values: { account: string; password: string }) {
    setError(null);
    setLoading(true);
    try {
      const tokens = await http<LoginResp>("/auth/login", {
        method: "POST",
        body: values,
      });
      setTokens(tokens.access_token, tokens.refresh_token);
      // 拉 /me 拿 user
      const me = await http<UserInfo>("/auth/me");
      setUser(me);
      navigate({ to: next });
    } catch (exc) {
      setError((exc as Error).message || t("auth.loginFailed"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card style={{ width: 380, maxWidth: "100%" }}>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          {t("auth.loginTitle")}
        </Typography.Title>
        <Typography.Text type="secondary">{t("auth.loginSubtitle")}</Typography.Text>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      <Form layout="vertical" onFinish={onFinish} requiredMark={false}>
        <Form.Item
          label={t("auth.account")}
          name="account"
          rules={[{ required: true, message: t("auth.accountRequired") }]}
        >
          <Input prefix={<UserOutlined />} placeholder={t("auth.accountPlaceholder")} />
        </Form.Item>
        <Form.Item
          label={t("auth.password")}
          name="password"
          rules={[{ required: true, message: t("auth.passwordRequired") }]}
        >
          <Input.Password prefix={<LockOutlined />} placeholder={t("auth.passwordPlaceholder")} />
        </Form.Item>
        <Button type="primary" htmlType="submit" block loading={loading}>
          {t("auth.loginButton")}
        </Button>
      </Form>

      <div
        style={{
          marginTop: 16,
          display: "flex",
          justifyContent: "flex-end",
          fontSize: 13,
        }}
      >
        {/* 找回密码不做(无 SMTP 通道,已决策):忘记密码请联系管理员在后台代重置 */}
        <Link to="/inx/register">{t("auth.toRegister")}</Link>
      </div>
    </Card>
  );
}
